import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Grid,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as PreviewIcon,
    Close as CloseIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    FileCopy as CopyIcon,
    Description as TemplateIcon
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiDetails } from '../../Constants';
import { showSnackbar } from '../../components/SnackBar/SnackBar';
import './TemplateManagement.css';
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


const TemplateManagement = ({ landlordID, propertyID, token }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const quillRef = useRef(null);

    // State management
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editorModal, setEditorModal] = useState(false);
    const [previewModal, setPreviewModal] = useState(false);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);

    // Editor state
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    const [templateContent, setTemplateContent] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Preview state
    const [previewContent, setPreviewContent] = useState('');
    const [templateToDelete, setTemplateToDelete] = useState(null);

    // Available placeholders
    const placeholders = [
        {
            category: 'Tenant Information',
            items: [
                { label: 'Tenant Name', value: '{{TENANT_NAME}}' },
                { label: 'Tenant ID', value: '{{TENANT_ID}}' },
                { label: 'Tenant Phone', value: '{{TENANT_PHONE}}' },
                { label: 'Tenant Email', value: '{{TENANT_EMAIL}}' }
            ]
        },
        {
            category: 'Landlord Information',
            items: [
                { label: 'Landlord Name', value: '{{LANDLORD_NAME}}' },
                { label: 'Landlord ID', value: '{{LANDLORD_ID}}' },
                { label: 'Landlord Phone', value: '{{LANDLORD_PHONE}}' },
                { label: 'Landlord Email', value: '{{LANDLORD_EMAIL}}' },
                { label: 'Landlord Address', value: '{{LANDLORD_ADDRESS}}' }
            ]
        },
        {
            category: 'Property Information',
            items: [
                { label: 'Property Name', value: '{{PROPERTY_NAME}}' },
                { label: 'Property Location', value: '{{PROPERTY_LOCATION}}' },
                { label: 'Unit Number', value: '{{UNIT_NUMBER}}' }
            ]
        },
        {
            category: 'Financial Information',
            items: [
                { label: 'Monthly Rent', value: '{{MONTHLY_RENT}}' },
                { label: 'Security Deposit', value: '{{SECURITY_DEPOSIT}}' },
                { label: 'Water Bill', value: '{{WATER_BILL}}' },
                { label: 'Electricity Bill', value: '{{ELECTRICITY_BILL}}' }
            ]
        },
        {
            category: 'Date Information',
            items: [
                { label: 'Current Date', value: '{{CURRENT_DATE}}' },
                { label: 'Agreement Date', value: '{{AGREEMENT_DATE}}' },
                { label: 'Lease Start Date', value: '{{LEASE_START_DATE}}' },
                { label: 'Lease End Date', value: '{{LEASE_END_DATE}}' },
                { label: 'Lease Period', value: '{{LEASE_PERIOD}}' },
                { label: 'Current Year', value: '{{CURRENT_YEAR}}' }
            ]
        }
    ];

    // Quill editor configuration
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ]
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'align',
        'list', 'bullet',
        'link'
    ];

    // Fetch templates on mount
    useEffect(() => {
        fetchTemplates();
    }, [landlordID, propertyID]);

    // Fetch all templates
    const fetchTemplates = async () => {
        setLoading(true);

        try {
            const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/templates/fetch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({
                    landlord_id: landlordID,
                    property_id: propertyID,
                    include_content: false
                })
            });

            // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }

            const data = await response.json();

            if (response.ok) {
                setTemplates(Array.isArray(data) ? data : (data.templates || []));
            } else {
                showSnackbar({
                    title: 'Error!',
                    message: data.message || 'Failed to load templates',
                    type: 'error',
                    position: { vertical: 'bottom', horizontal: 'right' }
                });
            }
        } catch (error) {
            console.error('Fetch templates error:', error);
            showSnackbar({
                title: 'Error!',
                message: 'Failed to connect to server',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' }
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch template with content
    const fetchTemplateContent = async (templateId) => {
        try {
            const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/templates/fetch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({
                    landlord_id: landlordID,
                    template_id: templateId,
                    include_content: true
                })
            });

            const data = await response.json();

            if (response.ok && data.templates && data.templates.length > 0) {
                return data.templates[0];
            }

            return null;
        } catch (error) {
            console.error('Fetch template content error:', error);
            return null;
        }
    };

    // Open editor for new template
    const handleCreateNew = () => {
        setCurrentTemplate(null);
        setTemplateName('');
        setTemplateDescription('');
        setTemplateContent(getDefaultTemplateContent());
        setIsDefault(false);
        setEditorModal(true);
    };

    // Open editor for existing template
    const handleEdit = async (template) => {
        const fullTemplate = await fetchTemplateContent(template.id);

        if (fullTemplate) {
            setCurrentTemplate(fullTemplate);
            setTemplateName(fullTemplate.template_name || '');
            setTemplateDescription(fullTemplate.description || '');
            setTemplateContent(fullTemplate.template_content || '');
            setIsDefault(fullTemplate.is_default || false);
            setEditorModal(true);
        } else {
            showSnackbar({
                title: 'Error!',
                message: 'Failed to load template content',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' }
            });
        }
    };

    // Save template
    const handleSave = async () => {
        // Validation
        if (!templateName.trim()) {
            showSnackbar({
                title: 'Validation Error',
                message: 'Please enter a template name',
                type: 'warning',
                position: { vertical: 'bottom', horizontal: 'right' }
            });
            return;
        }

        if (!templateContent.trim() || templateContent === '<p><br></p>') {
            showSnackbar({
                title: 'Validation Error',
                message: 'Template content cannot be empty',
                type: 'warning',
                position: { vertical: 'bottom', horizontal: 'right' }
            });
            return;
        }

        setSaving(true);

        try {
            const endpoint = currentTemplate
                ? 'pegasus/visionary/agreements/templates/update'
                : 'pegasus/visionary/agreements/templates/create';

            const requestBody = {
                landlord_id: landlordID,
                property_id: propertyID,
                template_name: templateName,
                template_content: templateContent,
                description: templateDescription,
                is_default: isDefault
            };

            if (currentTemplate) {
                requestBody.template_id = currentTemplate.id;
            }

            const response = await fetch(ApiDetails + endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (response.ok) {
                showSnackbar({
                    title: 'Success!',
                    message: data.message || 'Template saved successfully',
                    type: 'success',
                    position: { vertical: 'bottom', horizontal: 'right' }
                });

                setEditorModal(false);
                fetchTemplates();
            } else {
                showSnackbar({
                    title: 'Error!',
                    message: data.message || 'Failed to save template',
                    type: 'error',
                    position: { vertical: 'bottom', horizontal: 'right' }
                });
            }
        } catch (error) {
            console.error('Save template error:', error);
            showSnackbar({
                title: 'Error!',
                message: 'Failed to save template',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' }
            });
        } finally {
            setSaving(false);
        }
    };

    // Delete template
    const handleDelete = async () => {
        if (!templateToDelete) return;

        setDeleting(true);

        try {
            const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/templates/delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({
                    template_id: templateToDelete.id,
                    landlord_id: landlordID
                })
            });

            const data = await response.json();

            if (response.ok) {
                showSnackbar({
                    title: 'Success!',
                    message: 'Template deleted successfully',
                    type: 'success',
                    position: { vertical: 'bottom', horizontal: 'right' }
                });

                setDeleteConfirmModal(false);
                setTemplateToDelete(null);
                fetchTemplates();
            } else {
                showSnackbar({
                    title: 'Error!',
                    message: data.message || 'Failed to delete template',
                    type: 'error',
                    position: { vertical: 'bottom', horizontal: 'right' }
                });
            }
        } catch (error) {
            console.error('Delete template error:', error);
            showSnackbar({
                title: 'Error!',
                message: 'Failed to delete template',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' }
            });
        } finally {
            setDeleting(false);
        }
    };

    // Set as default
    const handleSetDefault = async (template) => {
        try {
            const response = await fetch(ApiDetails + 'pegasus/visionary/agreements/templates/update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': token
                },
                body: JSON.stringify({
                    template_id: template.id,
                    landlord_id: landlordID,
                    is_default: true
                })
            });

            const data = await response.json();

            if (response.ok) {
                showSnackbar({
                    title: 'Success!',
                    message: 'Default template updated',
                    type: 'success',
                    position: { vertical: 'bottom', horizontal: 'right' }
                });

                fetchTemplates();
            } else {
                showSnackbar({
                    title: 'Error!',
                    message: data.message || 'Failed to update default template',
                    type: 'error',
                    position: { vertical: 'bottom', horizontal: 'right' }
                });
            }
        } catch (error) {
            console.error('Set default error:', error);
            showSnackbar({
                title: 'Error!',
                message: 'Failed to set default template',
                type: 'error',
                position: { vertical: 'bottom', horizontal: 'right' }
            });
        }
    };

    // Preview template
    const handlePreview = async (template) => {
        const fullTemplate = await fetchTemplateContent(template.id);

        if (fullTemplate) {
            const sampleData = {
                '{{TENANT_NAME}}': 'John Doe',
                '{{TENANT_ID}}': '12345678',
                '{{TENANT_PHONE}}': '+254 712 345 678',
                '{{TENANT_EMAIL}}': 'john.doe@example.com',
                '{{LANDLORD_NAME}}': 'Jane Smith',
                '{{LANDLORD_ID}}': '87654321',
                '{{LANDLORD_PHONE}}': '+254 723 456 789',
                '{{LANDLORD_EMAIL}}': 'jane.smith@example.com',
                '{{LANDLORD_ADDRESS}}': 'Nairobi, Kenya',
                '{{PROPERTY_NAME}}': 'Sunset Apartments',
                '{{PROPERTY_LOCATION}}': 'Westlands, Nairobi',
                '{{UNIT_NUMBER}}': 'A-101',
                '{{MONTHLY_RENT}}': '50,000.00',
                '{{SECURITY_DEPOSIT}}': '50,000.00',
                '{{WATER_BILL}}': 'Included in rent',
                '{{ELECTRICITY_BILL}}': 'Tenant pays directly to KPLC',
                '{{CURRENT_DATE}}': new Date().toLocaleDateString(),
                '{{AGREEMENT_DATE}}': new Date().toLocaleDateString('en-US', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                }),
                '{{LEASE_START_DATE}}': '01/11/2025',
                '{{LEASE_END_DATE}}': '01/11/2026',
                '{{LEASE_PERIOD}}': '12 months',
                '{{CURRENT_YEAR}}': new Date().getFullYear().toString()
            };

            let preview = fullTemplate.template_content;
            Object.entries(sampleData).forEach(([placeholder, value]) => {
                const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                preview = preview.replace(regex, `<span style="background-color: #fff3cd; padding: 2px 4px;">${value}</span>`);
            });

            setPreviewContent(preview);
            setPreviewModal(true);
        }
    };

    // Insert placeholder into editor
    const insertPlaceholder = (placeholder) => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection();
            const position = range ? range.index : editor.getLength();
            editor.insertText(position, placeholder + ' ');
            editor.setSelection(position + placeholder.length + 1);
        }
    };

    // Get default template content
    const getDefaultTemplateContent = () => {
        return `<h1 style="text-align: center;">TENANCY AGREEMENT</h1>
<p style="text-align: center;">This agreement is made on <strong>{{AGREEMENT_DATE}}</strong></p>

<h2>BETWEEN:</h2>
<p><strong>{{LANDLORD_NAME}}</strong> (hereinafter called "the Landlord")</p>
<p>ID/Passport Number: {{LANDLORD_ID}}</p>
<p>Contact: {{LANDLORD_PHONE}} | Email: {{LANDLORD_EMAIL}}</p>

<h2>AND:</h2>
<p><strong>{{TENANT_NAME}}</strong> (hereinafter called "the Tenant")</p>
<p>ID/Passport Number: {{TENANT_ID}}</p>
<p>Contact: {{TENANT_PHONE}} | Email: {{TENANT_EMAIL}}</p>

<h2>1. PROPERTY DETAILS</h2>
<p>The Landlord agrees to let and the Tenant agrees to take on a monthly tenancy the premises located at:</p>
<ul>
  <li><strong>Property Name:</strong> {{PROPERTY_NAME}}</li>
  <li><strong>Unit Number:</strong> {{UNIT_NUMBER}}</li>
  <li><strong>Location:</strong> {{PROPERTY_LOCATION}}</li>
</ul>

<h2>2. RENTAL TERMS</h2>
<ol>
  <li><strong>Monthly Rent:</strong> KES {{MONTHLY_RENT}} payable in advance on or before the 5th day of each month.</li>
  <li><strong>Security Deposit:</strong> KES {{SECURITY_DEPOSIT}} (refundable at end of tenancy subject to property condition).</li>
  <li><strong>Water Bill:</strong> {{WATER_BILL}}</li>
  <li><strong>Electricity:</strong> {{ELECTRICITY_BILL}}</li>
  <li><strong>Lease Period:</strong> {{LEASE_PERIOD}}</li>
</ol>

<h2>3. TENANT OBLIGATIONS</h2>
<ol>
  <li>To pay rent promptly on the due date.</li>
  <li>To keep the premises in good and tenantable condition.</li>
  <li>To use the premises for residential purposes only.</li>
  <li>Not to sublet or assign the premises without written consent from the Landlord.</li>
</ol>

<h2>4. LANDLORD OBLIGATIONS</h2>
<ol>
  <li>To ensure the tenant enjoys quiet possession of the premises.</li>
  <li>To maintain the structure and exterior of the building.</li>
  <li>To carry out necessary repairs within a reasonable time.</li>
</ol>

<h2>5. TERMINATION</h2>
<p>Either party may terminate this agreement by giving ONE (1) calendar month written notice to the other party.</p>

<div style="margin-top: 60px;">
  <p><strong>LANDLORD</strong></p>
  <p>Signature: _______________________</p>
  <p>Name: {{LANDLORD_NAME}}</p>
  <p>Date: _________________________</p>
  <br />
  <p><strong>TENANT</strong></p>
  <p>Signature: _______________________</p>
  <p>Name: {{TENANT_NAME}}</p>
  <p>Date: _________________________</p>
</div>`;
    };

    // Close modals
    const closeModals = () => {
        setEditorModal(false);
        setPreviewModal(false);
        setDeleteConfirmModal(false);
        setTemplateToDelete(null);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                    Agreement Templates
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Create and manage custom templates for your tenant agreements
                </Typography>
            </Box>

            {/* Action Bar */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateNew}
                    sx={{
                        bgcolor: '#398DD2',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#2c6ba0' }
                    }}
                >
                    Create New Template
                </Button>
            </Box>

            {/* Templates List */}
            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading templates...</Typography>
                </Box>
            ) : templates.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        textAlign: 'center',
                        py: 8,
                        bgcolor: '#f5f5f5',
                        border: '1px dashed #ccc',
                        borderRadius: 2
                    }}
                >
                    <TemplateIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1, color: '#666' }}>
                        No Templates Yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Create your first agreement template to get started
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#398DD2',
                            color: '#398DD2'
                        }}
                    >
                        Create Template
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {templates.map((template) => (
                        <Grid item xs={12} key={template.id}>
                            <Card
                                sx={{
                                    borderLeft: template.is_default ? '4px solid #28a745' : 'none',
                                    '&:hover': { boxShadow: 4 }
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {template.template_name}
                                            </Typography>
                                            {template.is_default && (
                                                <Chip
                                                    label="Default"
                                                    color="success"
                                                    size="small"
                                                    icon={<StarIcon />}
                                                />
                                            )}
                                            {template.is_system_template && (
                                                <Chip
                                                    label="System"
                                                    size="small"
                                                    sx={{ bgcolor: '#6c757d', color: 'white' }}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    {template.description && (
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                            {template.description}
                                        </Typography>
                                    )}

                                    <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                        Created: {formatDate(template.created_at)}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<PreviewIcon />}
                                            onClick={() => handlePreview(template)}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Preview
                                        </Button>

                                        {template.can_edit !== false && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditIcon />}
                                                onClick={() => handleEdit(template)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Edit
                                            </Button>
                                        )}

                                        {!template.is_default && template.can_edit !== false && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<StarBorderIcon />}
                                                onClick={() => handleSetDefault(template)}
                                                sx={{ textTransform: 'none', color: '#28a745', borderColor: '#28a745' }}
                                            >
                                                Set as Default
                                            </Button>
                                        )}

                                        {!template.is_system_template && template.can_delete !== false && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => {
                                                    setTemplateToDelete(template);
                                                    setDeleteConfirmModal(true);
                                                }}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Editor Modal */}
            <Dialog
                open={editorModal}
                onClose={closeModals}
                maxWidth="xl"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{
                    color: '#398DD2',
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span>{currentTemplate ? 'Edit Template' : 'Create New Template'}</span>
                    <IconButton onClick={closeModals} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 3, height: '70vh', flexDirection: isMobile ? 'column' : 'row' }}>
                        {/* Placeholders Sidebar */}
                        <Paper
                            elevation={0}
                            sx={{
                                width: isMobile ? '100%' : 280,
                                maxHeight: isMobile ? 300 : '100%',
                                overflowY: 'auto',
                                p: 2,
                                bgcolor: '#f8f9fa',
                                border: '1px solid #dee2e6'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Insert Placeholders
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                Click to insert into template
                            </Typography>

                            {placeholders.map((group, idx) => (
                                <Box key={idx} sx={{ mb: 3 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                        {group.category}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {group.items.map((item, itemIdx) => (
                                            <Chip
                                                key={itemIdx}
                                                label={item.label}
                                                size="small"
                                                onClick={() => insertPlaceholder(item.value)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: '#398DD2',
                                                        color: 'white'
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            ))}

                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3cd', mt: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                    💡 How to use:
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ fontSize: '11px' }}>
                                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                                        <li>Click a chip to insert placeholder</li>
                                        <li>Placeholders look like: <code>{'{{TENANT_NAME}}'}</code></li>
                                        <li>They auto-fill with real data</li>
                                        <li>Don't modify placeholder text</li>
                                    </ul>
                                </Typography>
                            </Paper>
                        </Paper>

                        {/* Editor */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Template Details */}
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={8}>
                                        <TextField
                                            fullWidth
                                            label="Template Name"
                                            value={templateName}
                                            onChange={(e) => setTemplateName(e.target.value)}
                                            placeholder="e.g., Standard Residential Lease"
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isDefault}
                                                    onChange={(e) => setIsDefault(e.target.checked)}
                                                />
                                            }
                                            label="Set as default"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Description (Optional)"
                                            value={templateDescription}
                                            onChange={(e) => setTemplateDescription(e.target.value)}
                                            placeholder="Briefly describe this template"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Quill Editor */}
                            <Box sx={{ flex: 1, border: '1px solid #dee2e6', borderRadius: 1 }}>
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={templateContent}
                                    onChange={setTemplateContent}
                                    modules={modules}
                                    formats={formats}
                                    style={{ height: '100%' }}
                                    className="template-editor"
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button onClick={closeModals} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={() => {
                            const sampleData = {
                                '{{TENANT_NAME}}': 'John Doe',
                                '{{MONTHLY_RENT}}': '50,000.00'
                            };
                            let preview = templateContent;
                            Object.entries(sampleData).forEach(([k, v]) => {
                                preview = preview.replace(new RegExp(k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
                                    `<span style="background-color: #fff3cd;">${v}</span>`);
                            });
                            setPreviewContent(preview);
                            setPreviewModal(true);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Preview
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={saving}
                        onClick={handleSave}
                        sx={{
                            bgcolor: '#398DD2',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#2c6ba0' }
                        }}
                    >
                        Save Template
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* Preview Modal */}
            <Dialog
                open={previewModal}
                onClose={() => setPreviewModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span>Template Preview</span>
                    <IconButton onClick={() => setPreviewModal(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 2,
                            bgcolor: '#d1ecf1',
                            borderLeft: '4px solid #0c5460'
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Preview Mode:</strong> Highlighted text shows where placeholders will be replaced with actual data.
                        </Typography>
                    </Paper>
                    <Box
                        sx={{ p: 3, border: '1px solid #dee2e6', borderRadius: 1 }}
                        dangerouslySetInnerHTML={{ __html: previewContent }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog
                open={deleteConfirmModal}
                onClose={closeModals}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ color: '#d32f2f', fontWeight: 600 }}>
                    Delete Template
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this template? This action cannot be undone.
                    </Typography>
                    {templateToDelete && (
                        <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {templateToDelete.template_name}
                            </Typography>
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModals}>Cancel</Button>
                    <LoadingButton
                        loading={deleting}
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TemplateManagement;
